import { requireById } from './dom'
import { hideTip, showTip } from './admin-ui'

interface DayTraffic {
  label: string
  visits: number
  /** Set when a post went out this day — drawn as a gold milestone marker. */
  post?: string
}

/** Daily website visits, last 14 days. Traffic spikes follow the posts. */
const DAYS: readonly DayTraffic[] = [
  { label: '28 Jul', visits: 128 },
  { label: '29 Jul', visits: 142 },
  { label: '30 Jul', visits: 168, post: 'Tail-salute post' },
  { label: '31 Jul', visits: 335 },
  { label: '1 Aug', visits: 296 },
  { label: '2 Aug', visits: 231 },
  { label: '3 Aug', visits: 204 },
  { label: '4 Aug', visits: 228, post: 'Geoff’s big-lens post' },
  { label: '5 Aug', visits: 391 },
  { label: '6 Aug', visits: 344 },
  { label: '7 Aug', visits: 286 },
  { label: '8 Aug', visits: 312, post: 'Full-breach reel' },
  { label: '9 Aug', visits: 468 },
  { label: '10 Aug', visits: 342 },
]

/** Recent posts, best first — reach, reactions, and what they turned into. */
const POSTS = [
  {
    title: 'Full breach, boat-length away',
    where: 'Instagram + Facebook · 6.8K reach',
    stats: '♥ 212 · 31 comments · shared 18×',
    payoff: '→ 214 site visits · 9 booked',
  },
  {
    title: '27 trips, 27 whales — Geoff got his shot',
    where: 'Instagram · 3.1K reach',
    stats: '♥ 96 · 14 comments · shared 6×',
    payoff: '→ 88 site visits · 3 booked',
  },
  {
    title: 'Tail salute from the escort pod',
    where: 'Instagram + Facebook · 2.4K reach',
    stats: '♥ 74 · 9 comments · shared 4×',
    payoff: '→ 61 site visits · 2 booked',
  },
] as const

/* Chart geometry for the traffic line. */
const W = 560
const H = 230
const PAD_LEFT = 36
const PAD_TOP = 20
const PAD_BOTTOM = 30
const SCALE_MAX = 500
const TICKS = [0, 250, 500]

const x = (i: number): number => PAD_LEFT + (i * (W - PAD_LEFT - 14)) / (DAYS.length - 1)
const y = (v: number): number => H - PAD_BOTTOM - (v / SCALE_MAX) * (H - PAD_TOP - PAD_BOTTOM)

function trafficLine(): string {
  const baseline = H - PAD_BOTTOM

  const grid = TICKS.map((t) => {
    const gy = y(t)
    return (
      `<line x1="${PAD_LEFT}" y1="${gy.toFixed(1)}" x2="${W}" y2="${gy.toFixed(1)}" class="${t === 0 ? 'axis' : 'grid'}"/>` +
      `<text x="${PAD_LEFT - 6}" y="${(gy + 3.5).toFixed(1)}" class="ytick">${t}</text>`
    )
  }).join('')

  const points = DAYS.map((d, i) => `${x(i).toFixed(1)},${y(d.visits).toFixed(1)}`)
  const area = `<polygon points="${points.join(' ')} ${x(DAYS.length - 1).toFixed(1)},${baseline} ${PAD_LEFT},${baseline}" class="wash"/>`
  const line = `<polyline points="${points.join(' ')}" class="line"/>`

  // Sparse x labels — first, middle, today.
  const xticks = [0, 6, DAYS.length - 1]
    .map((i) => `<text x="${x(i).toFixed(1)}" y="${H - 8}" class="xtick">${DAYS[i]!.label}</text>`)
    .join('')

  // Direct-label the extreme only; the tooltip carries the rest.
  const peak = DAYS.reduce((a, d, i) => (d.visits > DAYS[a]!.visits ? i : a), 0)
  const peakLabel = `<text x="${x(peak).toFixed(1)}" y="${(y(DAYS[peak]!.visits) - 10).toFixed(1)}" class="cap">${DAYS[peak]!.visits}</text>`

  // Gold milestone markers on post days, labelled once (the latest post).
  let lastPost = -1
  DAYS.forEach((d, i) => {
    if (d.post) lastPost = i
  })
  const markers = DAYS.map((d, i) => {
    if (!d.post) return ''
    const mx = x(i).toFixed(1)
    const my = y(d.visits).toFixed(1)
    const last = i === lastPost
    const note = last
      ? `<text x="${mx}" y="${(y(d.visits) - 12).toFixed(1)}" class="mcap">${d.post}</text>`
      : ''
    return `<circle cx="${mx}" cy="${my}" r="4.5" class="post"/>` + note
  }).join('')

  // Full-height invisible hit bands so hover targets stay generous.
  const band = (W - PAD_LEFT - 14) / (DAYS.length - 1)
  const hits = DAYS.map((d, i) => {
    const tip = `${d.label} — ${d.visits} visits${d.post ? ` · posted: ${d.post}` : ''}`
    return (
      `<rect x="${(x(i) - band / 2).toFixed(1)}" y="0" width="${band.toFixed(1)}" height="${H}" class="hit" ` +
      `tabindex="0" role="img" aria-label="${tip}" data-tip="${tip}"/>`
    )
  }).join('')

  return `<svg class="tline" viewBox="0 0 ${W} ${H}" role="group" aria-label="Website visits per day with post milestones">${grid}${area}${line}${markers}${peakLabel}${xticks}${hits}</svg>`
}

/** Visually-hidden table twin, so every chart value is readable without the chart. */
function srTable(caption: string, rows: ReadonlyArray<readonly [string, string]>): string {
  return (
    `<table class="sr-only"><caption>${caption}</caption>` +
    `<tbody>${rows.map(([k, v]) => `<tr><th scope="row">${k}</th><td>${v}</td></tr>`).join('')}</tbody></table>`
  )
}

/**
 * The engagement view: how the outreach machine feeds the website. Traffic
 * with post milestones annotated, reach/followers/enquiries tiles, and each
 * post's payoff traced through to visits and bookings.
 */
export function initEngagement(): void {
  const body = requireById('engbody')

  body.innerHTML = `
    <div class="eng-tiles">
      <div class="tile">
        <label>Site visits — 30 days</label>
        <div class="tv">4,120</div>
        <div class="td up">↑ 38% vs July</div>
        <div class="ts">Top referrer: Instagram</div>
      </div>
      <div class="tile">
        <label>Social reach</label>
        <div class="tv">18.4K</div>
        <div class="td up">↑ 64% vs July</div>
        <div class="ts">Instagram + Facebook accounts reached</div>
      </div>
      <div class="tile">
        <label>Followers</label>
        <div class="tv">2,840</div>
        <div class="td up">↑ 312 this month</div>
        <div class="ts">Every post published by the studio below</div>
      </div>
      <div class="tile">
        <label>Enquiries started</label>
        <div class="tv">174</div>
        <div class="td up">↑ 41% vs July</div>
        <div class="ts">Web chats, DMs and calls — every one answered</div>
      </div>
    </div>
    <div class="eng-grid">
      <div class="chart-card">
        <div class="cc-head"><b>Website visits, day by day</b><span>last 14 days</span></div>
        <div class="cc-legend"><span><i class="key line"></i>Site visits</span><span><i class="key post"></i>Post published</span></div>
        ${trafficLine()}
        ${srTable('Website visits per day', DAYS.map((d) => [d.label, `${d.visits} visits${d.post ? ` — posted: ${d.post}` : ''}`] as const))}
      </div>
      <div class="chart-card eng-posts">
        <div class="cc-head"><b>Post performance</b><span>last 14 days</span></div>
        ${POSTS.map(
          (p) => `
        <div class="eng-post">
          <b>${p.title}</b>
          <span class="ep-where">${p.where}</span>
          <span class="ep-stats mono">${p.stats}</span>
          <span class="ep-payoff">${p.payoff}</span>
        </div>`,
        ).join('')}
      </div>
    </div>
    <div class="eng-insight">
      <label class="mono">Toura noticed</label>
      <p>The three days after a post average 2.1× the website traffic of quiet days — and Instagram is now your top referrer, ahead of Google. The full-breach reel alone brought 214 visitors, and 9 of them booked.</p>
    </div>`

  // Tooltips enhance the marks; the table twin carries every value.
  body.querySelectorAll<HTMLElement | SVGElement>('[data-tip]').forEach((mark) => {
    const tip = mark.getAttribute('data-tip') ?? ''
    const show = (): void => showTip(mark, tip)
    mark.addEventListener('mouseenter', show)
    mark.addEventListener('focus', show)
    mark.addEventListener('mouseleave', hideTip)
    mark.addEventListener('blur', hideTip)
  })
}
